'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function StatisticsCalculator() {
  const t = useTranslations("tools.statisticsCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [dataInput, setDataInput] = useState('10, 2, 38, 23, 38, 23, 21, 23');
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const parseData = (input: string): number[] => {
    const cleaned = input.replace(/[^\d\s,.\-+eE]/g, ' ');
    const values = cleaned.split(/[\s,]+/)
      .map(v => v.trim())
      .filter(v => v !== '')
      .map(v => parseFloat(v))
      .filter(v => !isNaN(v));

    return values;
  };

  const calculateMean = (data: number[]): number => {
    return data.reduce((sum, val) => sum + val, 0) / data.length;
  };

  const calculateMedian = (data: number[]): number => {
    const sorted = [...data].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);

    if (sorted.length % 2 === 0) {
      return (sorted[mid - 1] + sorted[mid]) / 2;
    }
    return sorted[mid];
  };

  const calculateMode = (data: number[]): string => {
    const frequency: { [key: string]: number } = {};
    let maxFreq = 0;

    data.forEach(val => {
      const key = val.toString();
      frequency[key] = (frequency[key] || 0) + 1;
      maxFreq = Math.max(maxFreq, frequency[key]);
    });

    if (maxFreq === 1) return t('noMode');

    const modes = Object.keys(frequency)
      .filter(key => frequency[key] === maxFreq)
      .map(key => parseFloat(key));

    return modes.length === data.length ? t('noMode') : modes.join(', ');
  };

  const calculateStdDev = (data: number[], isSample = false): number => {
    const mean = calculateMean(data);
    const squaredDiffs = data.map(val => Math.pow(val - mean, 2));
    const variance = squaredDiffs.reduce((sum, val) => sum + val, 0) / (data.length - (isSample ? 1 : 0));
    return Math.sqrt(variance);
  };

  const calculateVariance = (data: number[], isSample = false): number => {
    const mean = calculateMean(data);
    const squaredDiffs = data.map(val => Math.pow(val - mean, 2));
    return squaredDiffs.reduce((sum, val) => sum + val, 0) / (data.length - (isSample ? 1 : 0));
  };

  const calculateGeometricMean = (data: number[]): string | number => {
    if (data.some(val => val <= 0)) return t('naForNegative');
    const product = data.reduce((prod, val) => prod * val, 1);
    return Math.pow(product, 1 / data.length);
  };

  const formatNumber = (num: number | string): string => {
    if (typeof num === 'string') return num;
    if (Math.abs(num) < 0.0001 || Math.abs(num) > 1000000) {
      return num.toExponential(4);
    }
    return num.toFixed(4).replace(/\.?0+$/, '');
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const data = parseData(dataInput);

    if (data.length === 0) {
      setError(t('errorNoData'));
      setResults(null);
      return;
    }

    if (data.length === 1) {
      setError(t('errorMinData'));
      setResults(null);
      return;
    }

    const sorted = [...data].sort((a, b) => a - b);
    const mean = calculateMean(data);
    const median = calculateMedian(data);
    const mode = calculateMode(data);
    const sum = data.reduce((a, b) => a + b, 0);
    const sumSquares = data.reduce((a, b) => a + b * b, 0);
    const min = sorted[0];
    const max = sorted[sorted.length - 1];
    const range = max - min;
    const midrange = (min + max) / 2;
    const popStd = calculateStdDev(data, false);
    const sampleStd = calculateStdDev(data, true);
    const popVar = calculateVariance(data, false);
    const sampleVar = calculateVariance(data, true);
    const geoMean = calculateGeometricMean(data);

    setResults({
      mean,
      median,
      mode,
      sum,
      sumSquares,
      min,
      max,
      range,
      midrange,
      popStd,
      sampleStd,
      popVar,
      sampleVar,
      geoMean,
      count: data.length,
      sorted: sorted.slice(0, 10),
      hasMore: sorted.length > 10
    });
  };

  const clearData = () => {
    setDataInput('');
    setError('');
    setResults(null);
  };

  const loadSample = () => {
    setDataInput('10, 2, 38, 23, 38, 23, 21, 23');
    setError('');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '900px' },
    { maxWidth: '1000px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem' }
  );

  const sectionCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = { marginBottom: '1.5rem' };

  const labelStyle = getResponsiveStyle(
    { display: 'block', color: '#475569', fontWeight: 500, marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const textareaStyle = getResponsiveStyle(
    {
      width: '100%',
      minHeight: '150px',
      padding: '1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontFamily: "'Courier New', monospace",
      transition: 'border-color 0.3s',
      resize: 'vertical' as const,
      boxSizing: 'border-box' as const,
    },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const inputHintStyle = getResponsiveStyle(
    { color: '#64748b', marginTop: '0.5rem' },
    { fontSize: '0.8rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.85rem' }
  );

  const quickButtonsStyle = { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' as const, marginTop: '1rem' };

  const quickBtnStyle = getResponsiveStyle(
    {
      padding: '0.5rem 1rem',
      background: '#f1f5f9',
      border: '1px solid #cbd5e1',
      borderRadius: '0.5rem',
      color: '#475569',
      cursor: 'pointer',
      transition: 'all 0.3s',
    },
    { fontSize: '0.8rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.875rem' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.75rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      marginTop: '1rem',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' }
  );

  const errorMessageStyle = getResponsiveStyle(
    {
      background: '#fef2f2',
      border: '1px solid #fecaca',
      color: '#dc2626',
      borderRadius: '0.5rem',
      marginTop: '1rem',
    },
    { padding: '0.875rem', fontSize: '0.9rem' },
    { padding: '1rem', fontSize: '0.95rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const dataSummaryStyle = getResponsiveStyle(
    {
      background: '#f8fafc',
      borderRadius: '0.5rem',
      marginBottom: '1.5rem',
      border: '1px solid #e2e8f0',
    },
    { padding: '0.875rem' },
    { padding: '1rem' },
    { padding: '1rem' }
  );

  const dataSummaryItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    borderBottom: '1px solid #e2e8f0',
  };

  const dataSummaryLabelStyle = getResponsiveStyle(
    { color: '#64748b' },
    { fontSize: '0.85rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.9rem' }
  );

  const dataSummaryValueStyle = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 600 },
    { fontSize: '0.85rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.9rem' }
  );

  const resultsGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const resultCardStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderRadius: '0.75rem',
      borderLeft: '4px solid #2563eb',
    },
    { padding: '0.875rem' },
    { padding: '1rem' },
    { padding: '1rem' }
  );

  const resultCardHighlightStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
      borderRadius: '0.75rem',
      borderLeft: '4px solid #f59e0b',
    },
    { padding: '0.875rem' },
    { padding: '1rem' },
    { padding: '1rem' }
  );

  const resultLabelStyle = getResponsiveStyle(
    { display: 'block', color: '#64748b', fontWeight: 500, marginBottom: '0.25rem' },
    { fontSize: '0.8rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.875rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { display: 'block', color: colors.textPrimary, fontWeight: 700 },
    { fontSize: '1.25rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const distributionInfoStyle = getResponsiveStyle(
    {
      gridColumn: '1 / -1',
      background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
      borderRadius: '0.75rem',
      borderLeft: '4px solid #7c3aed',
    },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const distributionStatsStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(3, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const distributionStatStyle = { textAlign: 'center' as const };

  const distributionStatLabelStyle = getResponsiveStyle(
    { display: 'block', color: '#64748b', marginBottom: '0.25rem' },
    { fontSize: '0.75rem' },
    { fontSize: '0.8rem' },
    { fontSize: '0.8rem' }
  );

  const distributionStatValueStyle = getResponsiveStyle(
    { display: 'block', color: colors.textPrimary, fontWeight: 700 },
    { fontSize: '1rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.1rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const listStyle = getResponsiveStyle(
    { marginLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const listItemStyle = {
    marginBottom: '0.75rem',
    lineHeight: 1.6,
    color: colors.textSecondary,
  };

  const faqItemStyle = getResponsiveStyle(
    { marginBottom: '1.5rem' },
    {},
    { marginBottom: '1.75rem' },
    { marginBottom: '2rem' }
  );

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.2rem' },
    { fontSize: '1.3rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const referenceStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.4)',
      padding: '1rem',
      borderRadius: '0.5rem',
      marginBottom: '0.75rem',
      borderLeft: '3px solid #2563eb',
    },
    {},
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const referenceTitleStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.25rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1.1rem' }
  );

  const referenceLinkStyle = getResponsiveStyle(
    { color: colors.primary, textDecoration: 'none', wordBreak: 'break-all' as const },
    { fontSize: '0.85rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' }
  );

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>📊 {t('toolTitle')}</h2>
            <p style={summaryTextStyle}>{t('toolSubtitle')}</p>
          </div>

          <div style={calculatorLayoutStyle}>
            <div style={sectionCardStyle}>
              <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem' }}>📝 {t('inputSectionTitle')}</h3>

              <form onSubmit={handleCalculate}>
                <div style={formGroupStyle}>
                  <label htmlFor="data-input" style={labelStyle}>{t('dataValuesLabel')}</label>
                  <textarea
                    id="data-input"
                    style={textareaStyle}
                    placeholder={t('dataPlaceholder')}
                    value={dataInput}
                    onChange={(e) => setDataInput(e.target.value)}
                    onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                  <div style={inputHintStyle}>
                    💡 {t('inputHint')}
                  </div>
                </div>

                <div style={quickButtonsStyle}>
                  <button
                    type="button"
                    style={quickBtnStyle}
                    onClick={clearData}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#e2e8f0';
                      e.currentTarget.style.borderColor = '#94a3b8';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f1f5f9';
                      e.currentTarget.style.borderColor = '#cbd5e1';
                    }}
                  >
                    {t('clearButton')}
                  </button>
                  <button
                    type="button"
                    style={quickBtnStyle}
                    onClick={loadSample}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#e2e8f0';
                      e.currentTarget.style.borderColor = '#94a3b8';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f1f5f9';
                      e.currentTarget.style.borderColor = '#cbd5e1';
                    }}
                  >
                    {t('loadSampleButton')}
                  </button>
                </div>

                <button
                  type="submit"
                  style={calculateBtnStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 10px 20px -10px rgba(37, 99, 235, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {t('calculateButton')}
                </button>
              </form>

              {error && (
                <div style={errorMessageStyle}>
                  {error}
                </div>
              )}
            </div>

            {results && (
              <div style={sectionCardStyle}>
                <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem' }}>📈 {t('resultsSectionTitle')}</h3>

                <div style={dataSummaryStyle}>
                  <div style={dataSummaryItemStyle}>
                    <span style={dataSummaryLabelStyle}>{t('dataPointsLabel')}</span>
                    <span style={dataSummaryValueStyle}>{results.count} {t('valuesUnit')}</span>
                  </div>
                  <div style={{ ...dataSummaryItemStyle, borderBottom: 'none' }}>
                    <span style={dataSummaryLabelStyle}>{t('sortedDataLabel')}</span>
                    <span style={dataSummaryValueStyle}>
                      {results.sorted.map((v: number) => formatNumber(v)).join(', ')}
                      {results.hasMore ? '...' : ''}
                    </span>
                  </div>
                </div>

                <div style={resultsGridStyle}>
                  <div style={resultCardHighlightStyle}>
                    <span style={resultLabelStyle}>{t('meanLabel')}</span>
                    <span style={resultValueStyle}>{formatNumber(results.mean)}</span>
                  </div>

                  <div style={resultCardStyle}>
                    <span style={resultLabelStyle}>{t('medianLabel')}</span>
                    <span style={resultValueStyle}>{formatNumber(results.median)}</span>
                  </div>

                  <div style={resultCardStyle}>
                    <span style={resultLabelStyle}>{t('modeLabel')}</span>
                    <span style={resultValueStyle}>{results.mode}</span>
                  </div>

                  <div style={resultCardStyle}>
                    <span style={resultLabelStyle}>{t('rangeLabel')}</span>
                    <span style={resultValueStyle}>{formatNumber(results.range)}</span>
                  </div>

                  <div style={resultCardStyle}>
                    <span style={resultLabelStyle}>{t('sumLabel')}</span>
                    <span style={resultValueStyle}>{formatNumber(results.sum)}</span>
                  </div>

                  <div style={resultCardStyle}>
                    <span style={resultLabelStyle}>{t('countLabel')}</span>
                    <span style={resultValueStyle}>{results.count}</span>
                  </div>

                  <div style={resultCardHighlightStyle}>
                    <span style={resultLabelStyle}>{t('popStdLabel')}</span>
                    <span style={resultValueStyle}>{formatNumber(results.popStd)}</span>
                  </div>

                  <div style={resultCardStyle}>
                    <span style={resultLabelStyle}>{t('sampleStdLabel')}</span>
                    <span style={resultValueStyle}>{formatNumber(results.sampleStd)}</span>
                  </div>

                  <div style={resultCardStyle}>
                    <span style={resultLabelStyle}>{t('popVarLabel')}</span>
                    <span style={resultValueStyle}>{formatNumber(results.popVar)}</span>
                  </div>

                  <div style={resultCardStyle}>
                    <span style={resultLabelStyle}>{t('sampleVarLabel')}</span>
                    <span style={resultValueStyle}>{formatNumber(results.sampleVar)}</span>
                  </div>

                  <div style={resultCardStyle}>
                    <span style={resultLabelStyle}>{t('geoMeanLabel')}</span>
                    <span style={resultValueStyle}>
                      {typeof results.geoMean === 'string' ? results.geoMean : formatNumber(results.geoMean)}
                    </span>
                  </div>

                  <div style={resultCardStyle}>
                    <span style={resultLabelStyle}>{t('sumSquaresLabel')}</span>
                    <span style={resultValueStyle}>{formatNumber(results.sumSquares)}</span>
                  </div>

                  <div style={distributionInfoStyle}>
                    <h4 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1rem' : '1.25rem', marginBottom: '0.75rem' }}>
                      📊 {t('distributionTitle')}
                    </h4>
                    <div style={distributionStatsStyle}>
                      <div style={distributionStatStyle}>
                        <span style={distributionStatLabelStyle}>{t('minimumLabel')}</span>
                        <span style={distributionStatValueStyle}>{formatNumber(results.min)}</span>
                      </div>
                      <div style={distributionStatStyle}>
                        <span style={distributionStatLabelStyle}>{t('maximumLabel')}</span>
                        <span style={distributionStatValueStyle}>{formatNumber(results.max)}</span>
                      </div>
                      <div style={distributionStatStyle}>
                        <span style={distributionStatLabelStyle}>{t('midRangeLabel')}</span>
                        <span style={distributionStatValueStyle}>{formatNumber(results.midrange)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToStep1')}</li>
          <li style={listItemStyle}>{t('howToStep2')}</li>
          <li style={listItemStyle}>{t('howToStep3')}</li>
          <li style={listItemStyle}>{t('howToStep4')}</li>
          <li style={listItemStyle}>{t('howToStep5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>

        <h3 style={h3Style}>{t('keyBenefitsTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('benefit1')}</li>
          <li style={listItemStyle}>{t('benefit2')}</li>
          <li style={listItemStyle}>{t('benefit3')}</li>
          <li style={listItemStyle}>{t('benefit4')}</li>
        </ul>

        <h3 style={h3Style}>{t('modernFeaturesTitle')}</h3>
        <p style={paragraphStyle}>{t('modernFeaturesParagraph')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('feature1')}</li>
          <li style={listItemStyle}>{t('feature2')}</li>
          <li style={listItemStyle}>{t('feature3')}</li>
          <li style={listItemStyle}>{t('feature4')}</li>
        </ul>

        <h3 style={h3Style}>{t('bestPracticesTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('practice1')}</li>
          <li style={listItemStyle}>{t('practice2')}</li>
          <li style={listItemStyle}>{t('practice3')}</li>
          <li style={listItemStyle}>{t('practice4')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('measuresTitle')}</h3>
        <p style={paragraphStyle}>{t('measuresIntro')}</p>

        <h3 style={h3Style}>{t('centralTendencyTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong>{t('meanTitle')}</strong> {t('meanDescription')}</li>
          <li style={listItemStyle}><strong>{t('medianTitle')}</strong> {t('medianDescription')}</li>
          <li style={listItemStyle}><strong>{t('modeTitle')}</strong> {t('modeDescription')}</li>
        </ul>

        <h3 style={h3Style}>{t('dispersionTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong>{t('rangeTitle')}</strong> {t('rangeDescription')}</li>
          <li style={listItemStyle}><strong>{t('varianceTitle')}</strong> {t('varianceDescription')}</li>
          <li style={listItemStyle}><strong>{t('stdDevTitle')}</strong> {t('stdDevDescription')}</li>
        </ul>

        <h3 style={h3Style}>{t('otherMeasuresTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong>{t('geoMeanTitle')}</strong> {t('geoMeanDescription')}</li>
          <li style={listItemStyle}><strong>{t('sumSquaresTitle')}</strong> {t('sumSquaresDescription')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq5Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq6Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>

        <div style={referenceStyle}>
          <div style={referenceTitleStyle}>{t('ref1Title')}</div>
          <a
            href="https://purecalculators.com/article/benefits-using-statistics-alculators"
            target="_blank"
            rel="noopener noreferrer"
            style={referenceLinkStyle}
          >
            https://purecalculators.com/article/benefits-using-statistics-alculators
          </a>
        </div>

        <div style={referenceStyle}>
          <div style={referenceTitleStyle}>{t('ref2Title')}</div>
          <a
            href="https://elektronikaupraksi.com/software/best-statistics-calculator-online/"
            target="_blank"
            rel="noopener noreferrer"
            style={referenceLinkStyle}
          >
            https://elektronikaupraksi.com/software/best-statistics-calculator-online/
          </a>
        </div>

        <div style={referenceStyle}>
          <div style={referenceTitleStyle}>{t('ref3Title')}</div>
          <a
            href="https://www.youtube.com/watch?v=xcxm3wq5EYc"
            target="_blank"
            rel="noopener noreferrer"
            style={referenceLinkStyle}
          >
            https://www.youtube.com/watch?v=xcxm3wq5EYc
          </a>
        </div>

        <div style={referenceStyle}>
          <div style={referenceTitleStyle}>{t('ref4Title')}</div>
          <a
            href="https://www.edisonos.com/online-teaching/graphing-calculator"
            target="_blank"
            rel="noopener noreferrer"
            style={referenceLinkStyle}
          >
            https://www.edisonos.com/online-teaching/graphing-calculator
          </a>
        </div>

        <div style={{ ...referenceStyle, marginBottom: 0 }}>
          <div style={referenceTitleStyle}>{t('ref5Title')}</div>
          <a
            href="https://healthandcare.fitness.blog/2025/01/29/the-ultimate-guide-to-online-scientific-calculators-features-benefits-and-best-picks/"
            target="_blank"
            rel="noopener noreferrer"
            style={referenceLinkStyle}
          >
            https://healthandcare.fitness.blog/2025/01/29/the-ultimate-guide-to-online-scientific-calculators-features-benefits-and-best-picks/
          </a>
        </div>
      </section>
    </div>
  );
}